import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Popover, Spin, Tooltip, message } from 'antd';
import { CloseOutlined, PlusOutlined, TagOutlined } from '@ant-design/icons';
import api from '../../services/api';
import styles from './TaskTagsEditor.module.scss';

const TAG_COLORS = ['#e0f2fe', '#dcfce7', '#fef3c7', '#fee2e2', '#ede9fe', '#cffafe', '#fce7f3', '#e2e8f0'];

function normalizeTitle(title) {
    return String(title || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function normalizeTags(tags) {
    const unique = new Map();

    (Array.isArray(tags) ? tags : []).forEach((tag) => {
        const title = String(tag?.title || tag?.label || tag?.name || '').trim();
        if (!title) return;
        const key = tag?.id ? `id:${tag.id}` : `title:${normalizeTitle(title)}`;
        unique.set(key, {
            id: tag?.id ?? null,
            title,
            backgroundColor: tag?.backgroundColor || tag?.color || TAG_COLORS[0],
        });
    });

    return Array.from(unique.values()).sort((left, right) => left.title.localeCompare(right.title, 'ru'));
}

function hasTag(tags, candidate) {
    const candidateId = candidate?.id ? String(candidate.id) : '';
    const candidateTitle = normalizeTitle(candidate?.title);

    return tags.some((tag) => (
        (candidateId && String(tag.id) === candidateId)
        || normalizeTitle(tag.title) === candidateTitle
    ));
}

export default function TaskTagsEditor({
    value = [],
    onSave,
    disabled = false,
    className = '',
}) {
    const [open, setOpen] = useState(false);
    const [catalog, setCatalog] = useState([]);
    const [query, setQuery] = useState('');
    const [newTitle, setNewTitle] = useState('');
    const [selectedColor, setSelectedColor] = useState(TAG_COLORS[0]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const tags = useMemo(() => normalizeTags(value), [value]);

    const loadCatalog = useCallback(async (search = '') => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (search.trim()) params.set('q', search.trim());
            const response = await api.get(`/api/task-tags${params.toString() ? `?${params.toString()}` : ''}`);
            setCatalog(normalizeTags(response.data));
        } catch (error) {
            console.error('Ошибка загрузки тегов:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!open) return;
        const timerId = window.setTimeout(() => {
            void loadCatalog(query);
        }, 160);

        return () => window.clearTimeout(timerId);
    }, [loadCatalog, open, query]);

    useEffect(() => {
        if (open) {
            setNewTitle(query);
        }
    }, [open, query]);

    const saveTags = async (nextTags) => {
        if (typeof onSave !== 'function') return;

        try {
            setSaving(true);
            await onSave(normalizeTags(nextTags));
        } catch (error) {
            console.error('Ошибка сохранения тегов:', error);
            message.error('Не удалось сохранить теги');
        } finally {
            setSaving(false);
        }
    };

    const addExisting = async (tag) => {
        if (!tag || hasTag(tags, tag)) return;
        await saveTags([...tags, tag]);
        setQuery('');
        setNewTitle('');
    };

    const removeTag = async (tag) => {
        const nextTags = tags.filter((item) => {
            if (tag.id && item.id) return String(item.id) !== String(tag.id);
            return normalizeTitle(item.title) !== normalizeTitle(tag.title);
        });
        await saveTags(nextTags);
    };

    const createTag = async () => {
        const title = String(newTitle || query || '').trim();
        if (!title) return;

        try {
            setSaving(true);
            const response = await api.post('/api/task-tags', {
                title,
                backgroundColor: selectedColor,
            });
            const tag = normalizeTags([response.data])[0];
            if (tag) {
                const nextTags = hasTag(tags, tag) ? tags : [...tags, tag];
                if (typeof onSave === 'function') {
                    await onSave(normalizeTags(nextTags));
                }
                setCatalog((prev) => normalizeTags([...prev, tag]));
                setQuery('');
                setNewTitle('');
            }
        } catch (error) {
            console.error('Ошибка создания тега:', error);
            message.error('Не удалось создать тег');
        } finally {
            setSaving(false);
        }
    };

    const availableTags = catalog.filter((tag) => !hasTag(tags, tag));
    const normalizedNewTitle = normalizeTitle(newTitle || query);
    const canCreate = normalizedNewTitle && !hasTag([...catalog, ...tags], { title: newTitle || query });

    const content = (
        <div className={styles.dropdown}>
            <Input
                value={query}
                onChange={(event) => {
                    setQuery(event.target.value);
                    setNewTitle(event.target.value);
                }}
                placeholder="Найти или создать тег"
                allowClear
            />

            <div className={styles.palette}>
                {TAG_COLORS.map((color) => (
                    <button
                        key={color}
                        type="button"
                        className={color === selectedColor ? styles.colorActive : styles.color}
                        style={{ backgroundColor: color }}
                        onClick={() => setSelectedColor(color)}
                        aria-label={`Цвет ${color}`}
                    />
                ))}
            </div>

            <Spin spinning={loading}>
                <div className={styles.catalog}>
                    {availableTags.length > 0 ? availableTags.map((tag) => (
                        <button
                            key={tag.id || tag.title}
                            type="button"
                            className={styles.option}
                            onClick={() => void addExisting(tag)}
                        >
                            <span style={{ backgroundColor: tag.backgroundColor }}>{tag.title}</span>
                        </button>
                    )) : (
                        <div className={styles.empty}>Подходящих тегов нет</div>
                    )}
                </div>
            </Spin>

            <Button
                block
                icon={<PlusOutlined />}
                type={canCreate ? 'primary' : 'default'}
                onClick={() => void createTag()}
                disabled={!canCreate || saving}
                loading={saving}
            >
                Создать тег
            </Button>
        </div>
    );

    return (
        <div className={`${styles.wrapper} ${className}`}>
            <div className={styles.tags}>
                {tags.length > 0 ? tags.map((tag) => (
                    <span
                        key={tag.id || tag.title}
                        className={styles.tag}
                        style={{ backgroundColor: tag.backgroundColor }}
                    >
                        {tag.title}
                        {!disabled ? (
                            <button
                                type="button"
                                onClick={() => void removeTag(tag)}
                                disabled={saving}
                                aria-label={`Удалить тег ${tag.title}`}
                            >
                                <CloseOutlined />
                            </button>
                        ) : null}
                    </span>
                )) : (
                    <span className={styles.placeholder}>—</span>
                )}
            </div>

            {!disabled ? (
                <Popover
                    open={open}
                    onOpenChange={setOpen}
                    trigger="click"
                    content={content}
                    placement="bottomLeft"
                    overlayClassName={styles.popover}
                >
                    <Tooltip title="Добавить тег">
                        <Button size="small" icon={<TagOutlined />} disabled={saving} />
                    </Tooltip>
                </Popover>
            ) : null}
        </div>
    );
}
